import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { NotificationApiHandlerService } from '../notification-api-handler.service';
import { NotificationElement, UserAlert } from '../notification.model';
import {
  debounceTime,
  distinctUntilChanged,
  fromEvent,
  map,
  startWith,
  Subscription,
  switchMap,
} from 'rxjs';
import { NotificationService } from '../notification.service';
import { animate, style, transition, trigger } from '@angular/animations';

@Component({
  selector: 'app-notification-popup',
  templateUrl: './notification-popup.component.html',
  styleUrls: ['./notification-popup.component.scss'],
  animations: [
    trigger('fadeOut', [
      transition(':leave', [
        animate(
          '300ms ease-in',
          style({ opacity: 0, transform: 'scale(0.95) translateX(-100%)' })
        ),
      ]),
    ]),
  ],
})
export class NotificationPopupComponent implements OnDestroy {
  get isAllNotification(): boolean {
    return this.data === true;
  }

  notification: NotificationElement[] = [];
  isLoadingNotification: boolean = false;
  hasMoreNotifications: boolean = true;
  showAlertSettings: boolean = false;
  userAlertSettings!: UserAlert;
  notificationSearch: ElementRef | undefined;
  private _subscriptions: Subscription[] = [];
  payload = {
    pageSize: 20,
    pageNumber: 1,
    searchQuery: '',
  };

  @ViewChild('notificationSearch') set setNotificationSearch(
    ref: ElementRef | undefined
  ) {
    if (ref != null && this.isAllNotification) {
      this.notificationSearch = ref;
    }
  }
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: boolean,
    public notifyService: NotificationService,
    public dialogRef: MatDialogRef<NotificationPopupComponent>,
    private readonly dialog: MatDialog,
    private readonly notificationApiHandlerService: NotificationApiHandlerService
  ) {}

  ngOnInit(): void {
    this.userAlertSettings = this.notifyService.userAlertSettings;
    this.showAlertSettings = false;
    if (!this.isAllNotification) {
      this.notification = [];
      this.getAllNotifications(this.payload);
    }
  }

  private initializeSearchListener() {
    if (this.isAllNotification) {
      fromEvent(this.notificationSearch?.nativeElement, 'input')
        .pipe(
          startWith<any>(''),
          debounceTime(500),
          map(() => this.notificationSearch?.nativeElement.value?.trim() ?? ''),
          distinctUntilChanged(),
          switchMap((query: string) => {
            this.payload.pageNumber = 1;
            this.payload.searchQuery = query;

            this.notification = [];
            this.hasMoreNotifications = true;
            this.getAllNotifications(this.payload);
            return query;
          })
        )
        .subscribe((searchQuery: string) => {
          console.log('searchQuery', searchQuery);
        });
    }
  }

  ngAfterViewInit(): void {
    this.initializeSearchListener();
  }

  getAllNotifications(payload: {
    pageSize: number;
    pageNumber: number;
    searchQuery: string;
  }) {
    this.isLoadingNotification = true;
    this._subscriptions.push(
      (this.isAllNotification
        ? this.notificationApiHandlerService.getNotifications(payload)
        : this.notificationApiHandlerService.getTopUnreadNotifications()
      ).subscribe({
        next: (response) => {
          this.isLoadingNotification = false;
          this.notifyService.processNotifications(
            response?.notifications ?? []
          );
          if (this.isAllNotification) {
            this.notification.push(...(response?.notifications ?? []));
            this.payload.pageNumber++;
            if (
              this.notification.length >=
              (response.total_count ?? this.notification?.length ?? 0)
            ) {
              this.hasMoreNotifications = false;
            }
          }
          if (!this.isAllNotification) {
            this.notification = response?.notifications ?? [];
          }
        },
        error: (error) => {
          this.isLoadingNotification = false;
          console.error('Error fetching notifications:', error);
        },
      })
    );
  }

  openNotificationPopup() {
    this.dialog.open(NotificationPopupComponent, {
      width: '65vw',
      height: '90vh',
      panelClass: 'custom-class',
      data: true,
    });
    this.dialogRef.close();
  }

  closeFn() {
    this.dialogRef.close();
  }

  clearSubscriptions() {
    if (this._subscriptions) {
      this._subscriptions.forEach((subscription) => {
        subscription.unsubscribe();
      });
      this._subscriptions = [];
    }
  }

  onScroll(event: any) {
    const target = event.target as HTMLElement;

    const atBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - 5;

    if (atBottom) {
      console.log('Scrolled to bottom, loading more notifications...');
      this.onScrollEnd();
    }
  }

  getPreviousNotification(index: number): NotificationElement | null {
    return index > 0 ? this.notification[index - 1] : null;
  }

  onScrollEnd() {
    if (this.isLoadingNotification || !this.hasMoreNotifications) return;

    this.getAllNotifications(this.payload);
    this.scrollDown();
  }

  scrollDown() {
    setTimeout(() => {
      document?.getElementById('notification-loader-ui')?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    });
  }

  markNotificationAsRead(notification: NotificationElement) {
    this.markNotificationsAsReadCall([notification._id]);
  }

  markUnreadAsRead() {
    this.markNotificationsAsReadCall(
      this.notification
        ?.filter((notif) => !notif.recipient.read_at)
        ?.map((notif) => notif._id)
    );
  }

  markNotificationsAsReadCall(
    notificationIds: string[] | undefined = undefined
  ) {
    this.notificationApiHandlerService
      .markNotificationsAsRead(notificationIds)
      .subscribe({
        next: (response) => {
          console.log('Notification marked as read:', response);
        },
        error: (error) => {
          console.error('Error marking notification as read:', error);
        },
      });

    this.markNotificationsAsRead(notificationIds);
  }

  deleteNotification(notification: NotificationElement) {
    this.deleteNotificationsCall([notification._id]);
  }

  deleteAllFromUnreadList() {
    this.deleteNotificationsCall(this.notification?.map((notif) => notif._id));
  }

  deleteNotificationsCall(notificationIds: string[] | undefined = undefined) {
    this.notificationApiHandlerService
      .deleteNotifications(notificationIds)
      .subscribe({
        next: (response) => {
          console.log('Notification deleted:', response);
        },
        error: (error) => {
          console.error('Error deleting notification:', error);
        },
      });

    this.removeNotification(notificationIds);
  }

  markNotificationsAsRead(notificationIds: string[] | undefined) {
    if (!notificationIds) {
      this.notification.forEach((notif) => {
        notif.recipient.read_at = new Date().toISOString();
      });
      return;
    }

    this.notification.forEach((notif) => {
      if (notificationIds.includes(notif._id)) {
        notif.recipient.read_at = new Date().toISOString();
      }
    });
  }

  removeNotification(notificationIds: string[] | undefined) {
    if (!notificationIds) {
      this.notification = [];
      return;
    }
    setTimeout(() => {
      this.notification = this.notification.filter(
        (notif) => !notificationIds.includes(notif._id)
      );
    }, 300);
  }

  openNotification(notification: NotificationElement) {
    if (!notification?.recipient?.read_at) {
      this.markNotificationAsRead(notification);
    }

    this.notifyService.openNotification(notification);
  }

  toggleNotificationsAlert() {
    this.showAlertSettings = !this.showAlertSettings;
  }

  confirmUserSettings() {
    if (this.userAlertSettings.receive_in_app_alert) {
      this.notifyService.checkUnreadNotifications();
    }

    this.notifyService.updateUserAlertSettings({
      receive_email_alert: this.userAlertSettings.receive_email_alert,
      receive_in_app_alert: this.userAlertSettings.receive_in_app_alert,
      has_seen_user_guide:
        this.notifyService.userAlertSettings.has_seen_user_guide,
    });

    this.toggleNotificationsAlert();
  }

  ngOnDestroy(): void {
    this.clearSubscriptions();
  }
}
